import { Text } from '@/components/ui/text'
import usePlatformColor from '@/lib/hooks/usePlatformColor'
import { ConversationMessage } from '@/redux/slices/aiChatSlice'
import { useTheme } from '@react-navigation/native'
import { last } from 'lodash-es'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { View, ViewStyle } from 'react-native'
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from 'react-native-reanimated'

type AIMessageStatusProps = {
  message: ConversationMessage
}

export default function AIMessageStatus({ message }: AIMessageStatusProps) {
  const { t } = useTranslation('aiChat')

  const displayableStep = useMemo(() => {
    let result: React.ReactNode | undefined = last(
      message.stepDetails
        .filter(step => !step.done)
        .map(step => t(`statusDisplay.${step.step}`, { defaultValue: '' }))
        .filter(Boolean)
    )

    if (!result) {
      if (
        message.stepDetails.some(step => !step.done && step.step === 'jiri') &&
        message.stepDetails.some(step => step.done && POSSIBLE_CONTENT_FETCH_STEPS.includes(step.step))
      ) {
        result = t('_after_content_fetched', { defaultValue: '' })
      }
    }

    return result
  }, [message.stepDetails, t])

  if (message.interrupted || message.done) return null

  const noContent = isMessagePartsEmpty(message.parts)

  return (
    <>
      {noContent && <LoadingIndicator className="mt-3" />}

      {displayableStep && (
        <Text className="text-md text-typography-400 bg-background-50 px-4 py-2 mt-3 self-start rounded-lg">
          {displayableStep}
        </Text>
      )}
    </>
  )
}

export const POSSIBLE_CONTENT_FETCH_STEPS = [
  'scrape_url',
  'parse_image',
  'parse_pdf',
  'parse_word',
  'parse_excel',
  'parse_ppt',
]

export function isMessagePartsEmpty(parts: ConversationMessage['parts']) {
  return parts.length === 0 || parts.every(item => (item.type === 'text' || item.type === 'thinking') && !item.content)
}

const DOT_SIZE = 6
const DOT_GAP = 4
const STEP = DOT_SIZE + DOT_GAP
const ANIMATION_DURATION = 1500
const ANIMATION_DELAY = 500

const DOT_STYLES: ViewStyle = {
  position: 'absolute',
  width: DOT_SIZE,
  height: DOT_SIZE,
  borderRadius: DOT_SIZE / 2,
}

function LoadingIndicator({ className }: { className?: string }) {
  const theme = useTheme()
  const [dotColorGray] = usePlatformColor('quaternaryLabel')
  const dotColorPrimary = theme.colors.primary

  const progress = useSharedValue(0)

  useEffect(() => {
    progress.value = withDelay(
      ANIMATION_DELAY,
      withRepeat(withTiming(1, { duration: ANIMATION_DURATION, easing: Easing.linear }), -1, true)
    )
  }, [progress])

  const firstDotStyle = useAnimatedStyle(() => {
    const translateX = interpolate(progress.value, [0, 0.15, 0.5, 1], [0, 0, STEP, 2 * STEP])
    return { transform: [{ translateX }] }
  })

  const secondDotStyle = useAnimatedStyle(() => {
    const translateX = interpolate(progress.value, [0, 0.5, 1], [0, 0, STEP])
    return { transform: [{ translateX }] }
  })

  return (
    <View
      className={className}
      style={{ flexDirection: 'row', alignItems: 'center', height: DOT_SIZE, width: DOT_SIZE * 3 + DOT_GAP * 2 }}
    >
      <View style={[DOT_STYLES, { left: 2 * STEP, backgroundColor: dotColorGray }]} />
      <Animated.View style={[DOT_STYLES, { left: STEP, backgroundColor: dotColorGray }, secondDotStyle]} />
      <Animated.View style={[DOT_STYLES, { left: 0, backgroundColor: dotColorPrimary }, firstDotStyle]} />
    </View>
  )
}
